
import React, { forwardRef } from "react";
import { Question, QuestionContent, isQuestionContent } from "@/types/test";
import { MultipleChoiceQuestion } from "./questions/MultipleChoiceQuestion";
import { EssayQuestion } from "./questions/EssayQuestion";
import { AudioQuestion } from "./questions/AudioQuestion";
import { VideoQuestion } from "./questions/VideoQuestion";
import { CodingQuestion } from "./questions/CodingQuestion";
import { VideoRecorderHandle } from "./media/VideoRecorder";

interface QuestionResponseProps {
  question: Question;
  response: any;
  onChange: (response: any) => void;
  testResponseId?: string;
  onUploadStateChange?: (isUploading: boolean) => void;
  onVideoRecordingStart?: () => void;
  onVideoRecordingStop?: () => void;
  onUploadProgress?: (questionId: string, status: 'pending' | 'complete' | 'failed', type: 'video' | 'audio') => void;
  isUploadInProgress?: boolean;
  isVideoRecorderReady?: boolean; // NEW PROP
}

export const QuestionResponse = forwardRef<VideoRecorderHandle, QuestionResponseProps>(
  ({ question, response, onChange, testResponseId, onUploadStateChange, onVideoRecordingStart, onVideoRecordingStop, onUploadProgress, isUploadInProgress = false, isVideoRecorderReady = true }, ref) => {
    const ensureQuestionContent = (content: QuestionContent | string | any): QuestionContent => {
      if (typeof content === 'string') {
        try {
          return JSON.parse(content);
        } catch {
          return { question: content };
        }
      }
      return isQuestionContent(content) ? content : { question: 'Question not available' };
    };

    console.log(`Rendering question ${question.id} of type ${question.question_type} with response:`, {
      responseType: typeof response,
      responseIsNull: response === null,
      responseIsObject: typeof response === 'object' && response !== null,
      responseObjectKeys: typeof response === 'object' && response !== null ? Object.keys(response) : 'N/A'
    });

    const renderQuestion = () => {
      const questionContent = ensureQuestionContent(question.content);
      
      switch (question.question_type) {
        case "multiple_choice":
          return (
            <MultipleChoiceQuestion
              content={questionContent}
              response={response}
              onChange={onChange}
            />
          );
        case "essay":
          return (
            <EssayQuestion
              content={questionContent}
              response={response}
              onChange={onChange}
            />
          );
        case "audio_response":
          return (
            <AudioQuestion
              content={questionContent}
              response={response}
              onChange={onChange}
              questionId={question.id}
              onUploadStateChange={onUploadStateChange}
              isUploadInProgress={isUploadInProgress}
            />
          );
        case "video_response":
          return (
            <VideoQuestion
              content={questionContent}
              response={response}
              onChange={onChange}
              questionId={question.id}
              testResponseId={testResponseId}
              ref={ref}
              onUploadStateChange={onUploadStateChange}
              onRecordingStart={onVideoRecordingStart}
              onRecordingStop={onVideoRecordingStop}
              onUploadProgress={onUploadProgress}
              isUploadInProgress={isUploadInProgress}
              isVideoRecorderReady={isVideoRecorderReady}
            />
          );
        case "coding":
          return (
            <CodingQuestion
              question={question}
              response={response}
              onChange={onChange}
            />
          );
        default:
          return <p>Unsupported question type: {question.question_type}</p>;
      }
    };

    return <div>{renderQuestion()}</div>;
  }
);

QuestionResponse.displayName = "QuestionResponse";
